import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Spinner } from "./Spinner";
import { getStoredLang } from "./LanguagePicker";

const OL = "https://openlibrary.org";

export function BookSearch({ onSelect, autoFocus = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setResults([]); setOpen(false); return; }
    const ctrl = new AbortController();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const lang = getStoredLang();
        const url = `${OL}/search.json?q=${encodeURIComponent(q)}&language=${lang}&limit=24&fields=key,title,author_name,first_publish_year,cover_i,number_of_pages_median,subject,language`;
        const res = await fetch(url, { signal: ctrl.signal });
        const data = await res.json();
        // Trust the server-side `language=` filter; OL search often omits
        // the `language` field per hit even when the record matches, so
        // client-side re-filtering used to drop almost every result.
        const filtered = (data.docs || []).slice(0, 8);
        setResults(filtered);
        setOpen(true);
        setActive(0);
      } catch (e) {
        if (e.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  const handlePick = async (doc) => {
    setOpen(false);
    setQ(doc.title || "");
    // Pull description from the work, then ask Open Library for editions
    // so we can default to the *newest* one's cover/year/page count.
    let description = "";
    let newest = null;
    try {
      if (doc.key) {
        const [workRes, edRes] = await Promise.all([
          fetch(`${OL}${doc.key}.json`),
          fetch(`${OL}${doc.key}/editions.json?limit=40`),
        ]);
        const work = await workRes.json();
        if (typeof work.description === "string") description = work.description;
        else if (work.description?.value) description = work.description.value;
        const editionsData = await edRes.json();
        newest = pickNewestEdition(editionsData.entries || []);
      }
    } catch { /* fall back to base work data */ }

    const newestYearMatch = newest?.publish_date?.match(/\d{4}/);
    const newestYear = newestYearMatch ? parseInt(newestYearMatch[0], 10) : null;
    const newestCoverId = newest?.covers?.find((c) => c > 0);

    onSelect({
      title: doc.title || "",
      author: (doc.author_name && doc.author_name[0]) || "",
      year: newestYear || doc.first_publish_year || "",
      pages: newest?.number_of_pages || doc.number_of_pages_median || "",
      cover_url: newestCoverId
        ? `https://covers.openlibrary.org/b/id/${newestCoverId}-L.jpg`
        : (doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg` : ""),
      description: description.split("---")[0].trim().slice(0, 1200),
      genre: inferGenre(doc.subject || []),
      openlibrary_key: doc.key || "",
    });
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); handlePick(results[active]); }
    if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative" data-testid="book-search-container">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search for a book — title, author, ISBN…"
          className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.12)] rounded-sm pl-9 pr-10 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
          data-testid="book-search-input"
        />
        {loading && <Spinner size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252]" />}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1.5 w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.12)] shadow-lg max-h-[420px] overflow-y-auto" data-testid="book-search-results">
          {results.map((doc, i) => (
            <button
              type="button"
              key={(doc.key || "") + i}
              onMouseEnter={() => setActive(i)}
              onClick={() => handlePick(doc)}
              className={`w-full flex items-start gap-3 p-3 text-left border-b border-[rgba(0,0,0,0.06)] last:border-0 transition-colors ${active === i ? "bg-[#E8DFC1]" : "bg-[#FAF5E6] hover:bg-[#E8DFC1]"}`}
              data-testid={`book-search-result-${i}`}
            >
              <div className="w-10 h-14 bg-[#E8DFC1] border border-[rgba(0,0,0,0.06)] flex-shrink-0 overflow-hidden">
                {doc.cover_i ? (
                  <img src={`https://covers.openlibrary.org/b/id/${doc.cover_i}-S.jpg`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-mono text-[8px] text-[#A3A3A3] uppercase">No cover</div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading text-base text-[#0A0A0A] leading-tight truncate">{doc.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mt-1 truncate">
                  {(doc.author_name && doc.author_name[0]) || "Unknown author"}
                  {doc.first_publish_year ? ` · ${doc.first_publish_year}` : ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const GENRE_MAP = [
  ["fantasy", "Fantasy"],
  ["science fiction", "Sci-Fi"],
  ["mystery", "Mystery"],
  ["thriller", "Mystery"],
  ["romance", "Romance"],
  ["biography", "Biography"],
  ["history", "History"],
  ["self-help", "Self-Help"],
  ["poetry", "Poetry"],
  ["young adult", "Young Adult"],
  ["juvenile", "Children's"],
  ["children", "Children's"],
  ["non-fiction", "Non-Fiction"],
  ["nonfiction", "Non-Fiction"],
  ["fiction", "Fiction"],
];

function inferGenre(subjects) {
  const text = subjects.join(" ").toLowerCase();
  for (const [needle, label] of GENRE_MAP) {
    if (text.includes(needle)) return label;
  }
  return "Fiction";
}

/**
 * Of all the editions returned by Open Library, pick the most recent one
 * that actually has a cover. Prefer English editions when years tie.
 */
function pickNewestEdition(editions) {
  const scored = editions
    .filter((e) => e && Array.isArray(e.covers) && e.covers.some((c) => c > 0))
    .map((e) => {
      const m = (e.publish_date || "").match(/\d{4}/);
      const year = m ? parseInt(m[0], 10) : 0;
      const isEng = (e.languages || []).some((l) => l?.key === "/languages/eng");
      return { e, year, isEng };
    })
    .filter((r) => r.year >= 1900 && r.year <= new Date().getFullYear() + 1);
  if (scored.length === 0) return null;
  scored.sort((a, b) => (b.year - a.year) || (Number(b.isEng) - Number(a.isEng)));
  return scored[0].e;
}
